import React, { useState } from 'react'
import { useTheme } from 'styled-components'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import {
	Box,
	InputAdornment,
	MenuItem,
	TextField,
	Grid,
	Button,
	Link,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions
} from '@material-ui/core'
import { alpha } from '@material-ui/core/styles'
import ArrowRightAltIcon from '@material-ui/icons/ArrowRightAlt'
import useIntl from 'hooks/useIntl'
import { pxToRem } from 'packages/nuggetai.ui-kit/themes/utils'
import * as XLSX from 'xlsx'
import { CoreApplicantItemFieldsFragment } from 'hooks/useApollo'

export interface FilterOptionsProps {
	filters: { label: string; value: string }[]
	field: string
	handleToggleOrder: () => void
	handleChangeSort: (event: React.ChangeEvent) => void
	talents: CoreApplicantItemFieldsFragment[]
	allApplicants: CoreApplicantItemFieldsFragment[]
	fetchAllApplicants: () => Promise<CoreApplicantItemFieldsFragment[]>
	resetExportMode: () => void
}
// export const exportToExcel = (talents: CoreApplicantItemFieldsFragment[]) => {
// 	const data = talents.map(talent => ({
// 		FirstName: talent.user?.firstName || '',
// 		LastName: talent.user?.lastName || '',
// 		score:
// 			talent?.score?.skills && talent.score.skills.length > 0
// 				? talent.score.skills[0].score
// 				: talent?.score?.score || 0,
// 		Progress: talent.progress || 0,
// 		AssessmentName: talent.pipelines?.map(p => p.name).join(', ') || '',
// 		CreatedAt: talent.createdAt ? new Date(talent.createdAt).toLocaleDateString() : ''
// 	}))

// 	const ws = XLSX.utils.json_to_sheet(data)
// 	const wb = XLSX.utils.book_new()
// 	XLSX.utils.book_append_sheet(wb, ws, 'Talents')

// 	// Prompt user for filename
// 	const filename = window.prompt('Enter a filename for the Excel file:', 'talents')
// 	if (filename) {
// 		XLSX.writeFile(wb, `${filename}.xlsx`)
// 	}
// }
const exportToExcel = (talents: CoreApplicantItemFieldsFragment[], filename: string) => {
	const data = talents.map(talent => ({
		FirstName: talent.user?.firstName || '',
		LastName: talent.user?.lastName || '',
		Score:
			talent?.score?.skills && talent.score.skills.length > 0
				? talent.score.skills[0].score
				: talent?.score?.score || 0,
		Progress: talent.progress || 0,
		AssessmentName: talent.pipelines?.map(p => p.name).join(', ') || '',
		CreatedAt: talent.createdAt ? new Date(talent.createdAt).toLocaleDateString() : ''
	}))

	const ws = XLSX.utils.json_to_sheet(data)
	const wb = XLSX.utils.book_new()
	XLSX.utils.book_append_sheet(wb, ws, 'Talents')
	XLSX.writeFile(wb, `${filename}.xlsx`)
}

export const FilterOptions: React.FC<FilterOptionsProps> = ({
	filters,
	handleChangeSort,
	handleToggleOrder,
	field,
	talents,
	allApplicants,
	fetchAllApplicants,
	resetExportMode
}) => {
	const intl = useIntl()
	const theme = useTheme()
	const order = field.split('_').slice(-1)[0]

	const [open, setOpen] = useState(false)
	const [filename, setFilename] = useState('talents')

	const handleExportClick = () => {
		setOpen(true)
	}

	const handleExportConfirm = async () => {
		const toExport = allApplicants.length > 0 ? allApplicants : await fetchAllApplicants()
		exportToExcel(toExport, filename.trim() || 'talents')
		setOpen(false)
		resetExportMode()
	}

	return (
		<>
			<Grid
				item
				container
				alignItems="center"
				spacing={1}
				justifyContent="flex-end"
				wrap="nowrap"
			>
				<Grid item>
					<TextField
						variant="filled"
						hiddenLabel
						onChange={handleChangeSort}
						value={field
							.split('_')
							.slice(0, field.split('_').length > 2 ? 2 : 1)
							.join('_')}
						InputProps={{
							sx: {
								borderRadius: '8px',
								height: '40px',
								border: `1px solid ${theme.border}`,
								color: theme.text.description,
								backgroundColor: alpha(theme.grey.fourth, 0.6),
								':hover': {
									backgroundColor: alpha(theme.grey.third, 0.6)
								}
							}
						}}
						SelectProps={{
							IconComponent: CustomDropDownIcon,
							renderValue: value => (
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										borderRadius: 'inherit',
										fontSize: pxToRem(14)
									}}
								>
									<InputAdornment position="start">
										{intl.formatMessage({ id: 'sortBy' })}:
									</InputAdornment>
									<strong>
										{intl.formatMessage({
											id: `teams.talent.filter.${(value as string)
												.toLowerCase()
												.replace('_', '')}`
										})}
									</strong>
								</Box>
							)
						}}
						select
					>
						{filters.map(({ label, value }) => (
							<MenuItem key={value} value={value}>
								{label}
							</MenuItem>
						))}
					</TextField>
				</Grid>

				<Grid item>
					<Button
						name="change order"
						aria-label="change order"
						sx={{
							border: `1px solid ${theme.border}`,
							borderRadius: '8px',
							height: '40px',
							minWidth: '37px',
							background: alpha(theme.grey.fourth, 0.6),
							':hover': {
								backgroundColor: alpha(theme.grey.third, 0.6)
							}
						}}
						variant="contained"
						color="secondary"
						size="small"
						onClick={handleToggleOrder}
					>
						<ArrowRightAltIcon
							sx={{
								fill: theme.text.description,
								transition: 'all 0.05s',
								transform: order === 'ASC' ? 'rotate(90deg)' : 'rotate(-90deg)',
								opacity: 0.7
							}}
						/>
					</Button>
				</Grid>

				<Grid item>
					<Link sx={{ cursor: 'pointer' }} onClick={handleExportClick}>
						Export as Excel
					</Link>
				</Grid>
			</Grid>

			{/* Export filename modal */}
			<Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
				{/* <DialogTitle>Export to Excel</DialogTitle> */}
				<DialogContent>
					<TextField
						autoFocus
						margin="dense"
						label="File Name"
						type="text"
						fullWidth
						placeholder="Enter file name"
						value={filename}
						onChange={e => setFilename(e.target.value)}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setOpen(false)} color="primary">
						Cancel
					</Button>
					<Button onClick={handleExportConfirm} color="primary" variant="contained">
						Export
					</Button>
				</DialogActions>
			</Dialog>
		</>
	)
}

const CustomDropDownIcon = () => {
	const theme = useTheme()
	return (
		<ExpandMoreIcon
			sx={{ ml: -3, mr: 1, mt: 0.2, fontSize: 16, fill: theme.text.description }}
		/>
	)
}

export default FilterOptions
